import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { inr, STAGE_LABEL, dateTime } from "@/lib/fmt";
import { PageHead } from "@/components/Layouts";
import { Stat, Section, StatusPill, Loading, Empty } from "@/components/Bits";

export function VerifyBanner() {
  const { user } = useAuth();
  if (!user || user.email_verified) return null;
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      const m = { sent: "Verification email sent.", not_configured: "Email is not configured — no email was sent.", skipped_reserved_domain: "Test email domains don't receive emails.", already_verified: "Already verified." };
      (data.email_status === "sent" ? toast.success : toast.warning)(m[data.email_status] || "The email could not be sent. Please try later.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#FBEBD9]" data-testid="verify-email-banner">
      <MailWarning className="text-[#9A5A1E]" /><p className="text-sm flex-1">Your email isn't verified yet. You'll need a verified email to send enquiries or submit listings.</p>
      <button className="btn-ghost btn-sm" onClick={resend} data-testid="resend-verification-btn">Resend email</button>
    </div>
  );
}

export function AccessBanner({ role }) {
  const { user } = useAuth();
  if (!user || user.is_demo || (user.roles || []).some((r) => r === "admin" || r === "super_admin")) return null;
  const a = (user.access || {})[role] || {};
  const active = a.status === "approved" && a.end && new Date(a.end) > new Date();
  if (active) return <div className="card p-4 mb-6 bg-[#E4EEE7] text-sm" data-testid="access-active-banner">Your {role} access is active until {new Date(a.end).toLocaleDateString("en-IN")}. <Link to="/dashboard/registration" className="underline">Plan & receipt</Link></div>;
  const msg = a.status === "suspended" ? "is suspended" : a.status === "rejected" ? "was not approved" : a.status === "approved" ? "has expired — renew to continue" : "is not active yet";
  const locked = role === "buyer" ? "Enquiries, offers and document requests are locked." : "You can prepare drafts, but cannot submit for publication or receive enquiries.";
  return (
    <div className="card p-4 mb-6 bg-[#FBEBD9] flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="access-locked-banner">
      <p className="text-sm flex-1">Your {role} access {msg}. {locked}</p>
      <Link to="/dashboard/registration" className="btn-accent btn-sm" data-testid="go-registration-btn">Registration status</Link>
    </div>
  );
}

function BuyerOverview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/saved"), api.get("/enquiries", { params: { as_role: "buyer" } }), api.get("/notifications"), api.get("/access-requests")])
      .then(([s, e, n, a]) => setD({ saved: s.data, enq: e.data, notes: n.data.slice(0, 5), access: a.data.filter((x) => x.buyer_id === user.id) }));
  }, [user.id]);
  if (!d) return <Loading />;
  return (
    <>
      {!user.onboarded && <div className="card p-5 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center"><p className="flex-1 text-sm">Tell us what you're looking for to get better matches. It takes a minute and you can skip it.</p><Link to="/dashboard/onboarding" className="btn-accent btn-sm" data-testid="start-onboarding-btn">Set preferences</Link></div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat label="Saved businesses" value={d.saved.length} testid="stat-saved" />
        <Stat label="Open enquiries" value={d.enq.filter((e) => !e.outcome && e.stage !== "closed").length} testid="stat-enquiries" />
        <Stat label="NDAs accepted" value={d.enq.filter((e) => e.nda_status === "accepted").length} />
        <Stat label="Document access granted" value={d.access.filter((a) => a.status === "granted").length} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Acquisition progress"><Pipeline enq={d.enq} /></Section>
        <Section title="Latest notifications" right={<Link to="/dashboard/notifications" className="text-xs text-[#B86545] font-semibold">All</Link>}>
          {d.notes.length ? d.notes.map((n) => <Link key={n.id} to={n.link || "#"} className="block py-2 border-b border-[#EFE6D8] last:border-0 text-sm"><b>{n.title}</b><p className="text-xs text-stone-500">{n.body} · {dateTime(n.created_at)}</p></Link>) : <p className="text-sm text-stone-500">Nothing new.</p>}
        </Section>
      </div>
    </>
  );
}

export function Pipeline({ enq }) {
  if (!enq.length) return <Empty title="No conversations yet" body="Enquiries you send or receive appear here." testid="pipeline-empty" />;
  return (
    <div className="space-y-2" data-testid="pipeline-list">{enq.map((e) => (
      <Link key={e.id} to={`/dashboard/enquiries/${e.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-[#F7F3EB] px-3 py-2.5 hover:bg-[#E9DDCB]">
        <div className="min-w-0"><p className="text-sm font-semibold truncate">{e.listing_title}</p><p className="text-xs text-stone-500">{e.buyer_name} · updated {dateTime(e.updated_at)}</p></div>
        <StatusPill s={e.outcome || STAGE_LABEL[e.stage]} />
      </Link>
    ))}</div>
  );
}

function SellerOverview() {
  const [d, setD] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/seller/listings"), api.get("/enquiries", { params: { as_role: "seller" } }), api.get("/access-requests"), api.get("/seller/tasks"), api.get("/my/billing")])
      .then(([l, e, a, t, b]) => setD({ listings: l.data, enq: e.data, access: a.data, tasks: t.data, bills: b.data }));
  }, []);
  if (!d) return <Loading />;
  const sum = (k) => d.listings.reduce((s, l) => s + (l[k] || 0), 0);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat label="Listings" value={d.listings.length} testid="stat-listings" /><Stat label="Views" value={sum("views")} /><Stat label="Saves" value={sum("saves")} />
        <Stat label="Enquiries" value={d.enq.length} />
        <Stat label="NDA accepted" value={d.enq.filter((e) => e.nda_status === "accepted").length} />
        <Stat label="Access requests pending" value={d.access.filter((a) => a.status === "requested").length} testid="stat-access-pending" />
        <Stat label="Open verification tasks" value={d.tasks.filter((t) => t.status === "open").length} />
        <Stat label="Fees outstanding" value={inr(d.bills.filter((b) => ["due", "invoiced", "overdue"].includes(b.status)).reduce((s, b) => s + b.total - b.paid_amount, 0), "₹0")} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Your listings" right={<Link to="/dashboard/listings" className="text-xs text-[#B86545] font-semibold">Manage</Link>}>
          {d.listings.length ? d.listings.map((l) => (
            <Link key={l.id} to={`/dashboard/listings/${l.id}`} className="flex justify-between items-center gap-2 py-2 border-b border-[#EFE6D8] last:border-0 text-sm">
              <span className="truncate">{l.title}</span><span className="flex gap-1 shrink-0"><StatusPill s={l.status} />{l.availability && <StatusPill s={l.availability} />}</span>
            </Link>)) : <Empty title="No listings yet" action={<Link to="/dashboard/listings" className="btn-primary btn-sm"><Plus size={14} /> Create listing</Link>} />}
        </Section>
        <Section title="Buyer pipeline"><Pipeline enq={d.enq} /></Section>
      </div>
    </>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const seller = user.active_mode === "seller";
  return (
    <div>
      <PageHead eyebrow={seller ? "Seller dashboard" : "Buyer dashboard"} title={`Namaste, ${user.name.split(" ")[0]}`} sub={seller ? "Your listings, buyers and next steps." : "Your saved businesses, conversations and progress."} />
      <VerifyBanner />
      <AccessBanner role={seller ? "seller" : "buyer"} />      {seller ? <SellerOverview /> : <BuyerOverview />}
    </div>
  );
}
